import React, { useEffect, useRef, useState } from "react";
import { AnalyticsService } from "@shared/services/analytics.service";
import type {
  DashboardData,
  OccupationProfileMetrics,
} from "@shared/services/analytics.service";
import { Tooltip } from "@shared/components/common/Tooltip";
import { useCountUp } from "./useCountUp";
import { getBrowserMeta, formatBrowserLabel } from "../components/BrowserBrand";
import { Skeleton, MetricsSkeleton } from "../components/Skeleton";
import UtmLinkGenerator from "../components/UtmLinkGenerator";
import { TRAFFIC_LABEL_COLORS, TRAFFIC_FALLBACK_COLORS } from "../components/PlatformColors";
import * as XLSX from "xlsx";
import {
  Users,
  Eye,
  MousePointerClick,
  Clock,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  Calendar,
  TrendingUp,
  MapPin,
  BarChart3,
  Activity,
  PlayCircle,
  CheckCircle2,
  UserPlus,
  RefreshCw,
  HelpCircle,
  Download,
  Share2,
} from "lucide-react";

declare global {
  interface Window {
    am4core: any;
    am4charts: any;
    am4themes_animated: any;
  }
}

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 30);
  return { startDate: toDateInputValue(start), endDate: toDateInputValue(end) };
};

const formatPageLabel = (path: string) => {
  if (path === "/") return "Landing Page";
  if (path === "/kapital") return "Kapital";
  if (path === "/valora") return "Valora";
  return path;
};

const MetricCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  subtitle?: string;
  tooltip?: string;
  compact?: boolean;
}> = ({ title, value, icon, subtitle, tooltip, compact = false }) => {
  const isNumeric = typeof value === "number";
  const numericValue = isNumeric ? value : parseFloat(String(value).replace(/[^0-9.-]/g, "")) || 0;
  const suffix = isNumeric ? "" : String(value).replace(/[\d.-]/g, "");
  const prefix = isNumeric ? "" : String(value).match(/^[^0-9]*/)?.[0] || "";
  const animated = useCountUp(numericValue);

  return (
    <div className={`rounded-xl bg-white border border-gray-100 ${compact ? "p-3 shadow-none border-0" : "p-5 shadow-sm"}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-gray-500">{title}</p>
            {tooltip && (
              <Tooltip id={`tooltip-${title}`} content={tooltip}>
                <HelpCircle className="h-3.5 w-3.5 text-gray-500 hover:text-gray-700 cursor-help transition-colors" />
              </Tooltip>
            )}
          </div>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {isNumeric ? animated : `${prefix}${animated}${suffix}`}
          </p>
          {subtitle && <p className="mt-1 text-xs text-gray-400">{subtitle}</p>}
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
          {icon}
        </div>
      </div>
    </div>
  );
};

const ProgressBar: React.FC<{ label: string; count: number; percentage: number; color?: string; icon?: React.ReactNode; barStyle?: React.CSSProperties }> = ({
  label,
  count,
  percentage,
  color = "bg-blue-500",
  icon,
  barStyle,
}) => (
  <div className="mb-3 min-w-0">
    <div className="mb-1 flex min-w-0 items-center gap-2 text-sm">
      {icon && <span className="flex h-4 w-4 shrink-0 items-center justify-center">{icon}</span>}
      <span className="min-w-0 flex-1 truncate font-medium text-gray-700" title={label}>{label}</span>
      <span className="shrink-0 text-gray-500">
        {count} ({percentage}%)
      </span>
    </div>
    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(percentage, 100)}%`, ...barStyle }} />
    </div>
  </div>
);

const sourceBarColor = (label: string, index: number) =>
  TRAFFIC_LABEL_COLORS[label] ??
  TRAFFIC_FALLBACK_COLORS[index % TRAFFIC_FALLBACK_COLORS.length];

const SectionCard: React.FC<{ title: string; children: React.ReactNode; icon?: React.ReactNode; tooltip?: string; className?: string }> = ({
  title,
  children,
  icon,
  tooltip,
  className = "",
}) => (
  <div className={`min-w-0 h-full rounded-xl bg-white p-5 shadow-sm border border-gray-100 ${className}`}>
    <div className="mb-4 flex items-center gap-2">
      {icon && <span className="text-gray-500">{icon}</span>}
      <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500">{title}</h3>
      {tooltip && (
        <Tooltip id={`tooltip-${title}`} content={tooltip}>
          <HelpCircle className="h-3.5 w-3.5 text-gray-500 hover:text-gray-700 cursor-help transition-colors" />
        </Tooltip>
      )}
    </div>
    {children}
  </div>
);

const ProfilePieChart: React.FC<{ title?: string; items: { label: string; count: number; percentage: number }[]; color: string; donut?: boolean; innerRadius?: number; showLegend?: boolean; chartHeight?: number; withBackground?: boolean }> = ({ title, items, color, donut = true, innerRadius = 58, showLegend = true, chartHeight, withBackground = false }) => {
  const chartDiv = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartDiv.current || !window.am4core || items.length === 0) return;
    const chart = window.am4core.create(chartDiv.current, window.am4charts.PieChart);
    if (donut) chart.innerRadius = window.am4core.percent(innerRadius);
    chart.paddingBottom = 12;
    chart.data = items.map((item) => ({ ...item, value: item.count }));

    const series = chart.series.push(new window.am4charts.PieSeries());
    series.dataFields.value = "value";
    series.dataFields.category = "label";
    series.labels.template.disabled = true;
    series.ticks.template.disabled = true;
    series.slices.template.tooltipText = "{category}: {value} ({percentage}%)";
    series.slices.template.stroke = window.am4core.color("#ffffff");
    series.slices.template.strokeWidth = 2;
    series.colors.list = [window.am4core.color(color), window.am4core.color("#7dd3fc"), window.am4core.color("#c4b5fd"), window.am4core.color("#a7f3d0"), window.am4core.color("#fcd34d")];
    if (showLegend) {
      chart.legend = new window.am4charts.Legend();
      chart.legend.position = "bottom";
      chart.legend.labels.template.text = "{name}";
      chart.legend.valueLabels.template.text = "{value} ({percentage}%)";
      chart.legend.labels.template.maxWidth = 130;
      chart.legend.labels.template.wrap = true;
      chart.legend.fontSize = 10;
    }
    series.appear(700, 80);

    return () => {
      // amCharts sigue procesando eventos durante la animación. Detenerlo
      // antes de liberar el gráfico evita callbacks sobre EventDispatcher ya
      // destruido al cambiar de página o de periodo.
      chart.dispose();
    };
  }, [items, color, donut, innerRadius, showLegend, chartHeight, withBackground]);

  return (
    <div className={`flex min-w-0 flex-col justify-center rounded-lg p-3 ${withBackground ? "bg-slate-50" : ""}`}>
      {title && <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{title}</p>}
      {items.length > 0 ? <div ref={chartDiv} style={chartHeight ? { width: "100%", height: chartHeight } : { width: "100%", aspectRatio: "1.15" }} /> : <p className="py-12 text-center text-sm text-gray-400">Sin datos</p>}  
    </div>
  );
};

// Pie SVG nativo relleno para el encabezado: amCharts sobredimensiona
// el gráfico en contenedores pequeños y deja su marca de agua. Aquí
// solo se ve el círculo y al pasar el mouse muestra nombre y porcentaje.
const AudienceDonut: React.FC<{ items: { label: string; count: number; percentage: number }[] }> = ({ items }) => {
  const total = items.reduce((sum, item) => sum + item.count, 0);
  const RADIUS = 48;
  const COLORS = ["#8b5cf6", "#7dd3fc", "#c4b5fd", "#a7f3d0", "#fcd34d"];
  const point = (angle: number): [number, number] => [
    50 + RADIUS * Math.cos(angle),
    50 + RADIUS * Math.sin(angle),
  ];
  let acc = 0;
  return (
    <svg viewBox="0 0 100 100" className="h-20 w-20 sm:h-24 sm:w-24" role="img">
      <circle cx="50" cy="50" r={RADIUS} fill="#e5e7eb" />
      {total > 0 &&
        items.map((item, index) => {
          if (item.count <= 0) return null;
          const fraction = item.count / total;
          const startAngle = -Math.PI / 2 + acc * 2 * Math.PI;
          acc += fraction;
          const endAngle = -Math.PI / 2 + acc * 2 * Math.PI;
          if (fraction >= 1) {
            return (
              <circle key={item.label} cx="50" cy="50" r={RADIUS} fill={COLORS[index % COLORS.length]}>
                <title>{`${item.label}: ${item.count} (${item.percentage}%)`}</title>
              </circle>
            );
          }
          const [x1, y1] = point(startAngle);
          const [x2, y2] = point(endAngle);
          return (
            <path
              key={item.label}
              d={`M 50 50 L ${x1} ${y1} A ${RADIUS} ${RADIUS} 0 ${fraction > 0.5 ? 1 : 0} 1 ${x2} ${y2} Z`}
              fill={COLORS[index % COLORS.length]}
              stroke="#ffffff"
              strokeWidth="1"
            >
              <title>{`${item.label}: ${item.count} (${item.percentage}%)`}</title>
            </path>
          );
        })}
    </svg>
  );
};

const OccupationProfileBreakdown: React.FC<{ data: OccupationProfileMetrics }> = ({ data }) => (
  <div className="relative min-w-0">
    <style>{`
      .perfil-charts-scroll { scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9; }
      .perfil-charts-scroll::-webkit-scrollbar { height: 8px; }
      .perfil-charts-scroll::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 9999px; }
      .perfil-charts-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 9999px; }
      .perfil-charts-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
    `}</style>
    {/* Fuera del flujo, anclado a la esquina superior derecha de la
        sección (a la altura del título), sin empujar los textos. */}
    <div className="absolute -top-8 right-2">
      <AudienceDonut items={data.audiences} />
    </div>
    <div className="mb-4 pr-24 sm:pr-28">
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Distribución general</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">
        {data.total_devices}{" "}
        <span className="text-sm font-medium text-gray-400">dispositivos</span>
      </p>
    </div>
    {/* Los 3 gráficos en una sola fila con desplazamiento horizontal:
        la barra inferior permite deslizar para verlos todos. El ancho
        fijo por tarjeta evita que la dona crezca sin límite. */}
    <div className="perfil-charts-scroll flex w-full gap-4 overflow-x-auto pb-3 pt-6">
      <div className="w-[270px] shrink-0 sm:w-[300px]">
        <ProfilePieChart title="Trabajadores por cargo" items={data.cargos ?? data.specialist_roles} color="#2563eb" donut={false} withBackground />
      </div>
      <div className="w-[270px] shrink-0 sm:w-[300px]">
        <ProfilePieChart title="Trabajadores por sector" items={data.sectors ?? data.company_names} color="#10b981" innerRadius={48} withBackground />
      </div>
      <div className="w-[270px] shrink-0 sm:w-[300px]">
        <ProfilePieChart title="Estudiantes por especialidad" items={data.especialidades ?? []} color="#8b5cf6" innerRadius={48} withBackground />
      </div>
    </div>
  </div>
);

const SessionsSummaryBar: React.FC<{ items: { label: string; value: string | number; subtitle?: string; tooltip: string; icon: React.ReactNode; color: string }[] }> = ({ items }) => {
  const segmentWeight = (value: string | number) => {
    const numericValue = Number.parseFloat(String(value).replace(/[^0-9.-]/g, "")) || 0;
    return Math.max(Math.log1p(Math.max(numericValue, 0)), 0.25);
  };

  return (
    <SectionCard title="Resumen de sesiones" icon={<Activity className="h-4 w-4" />} tooltip="Resumen de los indicadores principales del periodo seleccionado.">
      {items.length === 0 ? <p className="text-sm text-gray-400">Sin datos</p> : (
        <>
          <div className="mb-5 flex h-3 overflow-hidden rounded-full bg-gray-100">
            {items.map((item) => (
              <div key={item.label} className="min-w-[8px]" style={{ flex: segmentWeight(item.value), backgroundColor: item.color }} title={`${item.label}: ${item.value}`} />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-3">
            {items.map((item) => (
              <div key={item.label} className="flex min-w-0 items-center gap-2 text-sm">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
                <Tooltip id={`summary-${item.label}`} content={item.tooltip}>
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center text-gray-400">{item.icon}</span>
                </Tooltip>
                <span className="min-w-0 flex-1 truncate text-gray-700" title={item.subtitle}>{item.label}</span>
                <span className="font-medium text-gray-500">{item.value}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </SectionCard>
  );
};

const SessionsChart: React.FC<{ data: { date: string; count: number }[] }> = ({ data }) => {
  const chartDiv = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartDiv.current || !window.am4core || data.length === 0) return;

    const chart = window.am4core.create(chartDiv.current, window.am4charts.XYChart);
    chart.paddingRight = 20;

    const dateAxis = chart.xAxes.push(new window.am4charts.DateAxis());
    dateAxis.renderer.labels.template.rotation = -45;
    dateAxis.renderer.labels.template.verticalCenter = "middle";
    dateAxis.renderer.labels.template.horizontalCenter = "right";
    dateAxis.tooltipDateFormat = "yyyy-MM-dd";

    const valueAxis = chart.yAxes.push(new window.am4charts.ValueAxis());
    valueAxis.min = 0;

    const series = chart.series.push(new window.am4charts.LineSeries());
    series.dataFields.dateX = "date";
    series.dataFields.valueY = "count";
    series.strokeWidth = 3;
    series.stroke = window.am4core.color("#3699FF");
    series.fill = window.am4core.color("#3699FF");
    series.fillOpacity = 0.15;
    series.tensionX = 0.8;
    series.tooltipText = "{date}: {count} sesiones";

    const bullet = series.bullets.push(new window.am4charts.CircleBullet());
    bullet.circle.radius = 4;
    bullet.circle.fill = window.am4core.color("#3699FF");
    bullet.circle.stroke = window.am4core.color("#fff");
    bullet.circle.strokeWidth = 2;

    chart.cursor = new window.am4charts.XYCursor();
    chart.cursor.lineY.disabled = true;

    chart.data = data.map((d) => ({
      date: new Date(d.date.includes("T") ? d.date : `${d.date}T00:00:00`),
      count: d.count,
    }));

    // Animación de dibujado de línea de izquierda a derecha
    series.hide();
    let disposed = false;
    const animationTimer = window.setTimeout(() => {
      if (disposed || chart.isDisposed()) return;
      try {
        series.show();
        series.appear(1500, 100);
      } catch {
        // El componente puede desmontarse mientras amCharts finaliza su ciclo.
      }
    }, 200);

    return () => {
      disposed = true;
      window.clearTimeout(animationTimer);
      chart.dispose();
    };
  }, [data]);

  return <div ref={chartDiv} style={{ width: "100%", aspectRatio: "3" }} />;
};

const AnalyticsPage: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [error, setError] = useState<string | null>(null);
  const [activeSessions, setActiveSessions] = useState(0);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const result = await AnalyticsService.getDashboard(dateRange.startDate, dateRange.endDate);
        setData(result);
        setError(null);
      } catch (e: any) {
        setError(e.message || "Error cargando métricas");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [dateRange]);

  // Fetch sesiones activas cada 30 segundos
  useEffect(() => {
    const fetchActive = async () => {
      try {
        const result = await AnalyticsService.getActiveSessions();
        setActiveSessions(result.active_sessions);
      } catch {
        // Silenciar
      }
    };
    fetchActive();
    const interval = setInterval(fetchActive, 30000);
    return () => clearInterval(interval);
  }, []);

  const deviceIcon = (type: string) => {
    const t = type.toLowerCase();
    if (t.includes("mobile")) return <Smartphone className="h-4 w-4" />;
    if (t.includes("tablet")) return <Tablet className="h-4 w-4" />;
    return <Monitor className="h-4 w-4" />;
  };

  const deviceColor = (type: string) => {
    const t = type.toLowerCase();
    if (t.includes("mobile")) return "bg-emerald-500";
    if (t.includes("tablet")) return "bg-amber-500";
    return "bg-blue-500";
  };

  const exportToExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();

    // 1. Resumen / KPIs
    const summaryData = [
      { Métrica: "Sesiones Totales", Valor: data.summary.total_sessions },
      { Métrica: "Páginas Vistas", Valor: data.summary.total_page_views },
      { Métrica: "Visitantes Únicos", Valor: data.summary.unique_visitors },
      { Métrica: "Sesiones Activas", Valor: data.summary.active_sessions },
      { Métrica: "Eventos Totales", Valor: data.summary.total_events },
      { Métrica: "Visitantes que inician Kapital", Valor: data.kapital_funnel?.users_started ?? 0 },
      { Métrica: "Tasa de activación Kapital (%)", Valor: data.kapital_funnel?.activation_rate ?? 0 },
      { Métrica: "Cálculos Kapital iniciados", Valor: data.kapital_funnel?.started ?? 0 },
      { Métrica: "Cálculos Kapital completados", Valor: data.kapital_funnel?.completed ?? 0 },
      { Métrica: "Tasa de finalización Kapital (%)", Valor: data.kapital_funnel?.completion_rate ?? 0 },
      { Métrica: "Visitantes nuevos en Kapital", Valor: data.kapital_retention?.new_users ?? 0 },
      { Métrica: "Visitantes recurrentes en Kapital", Valor: data.kapital_retention?.recurring_users ?? 0 },
      { Métrica: "Captaciones WhatsApp (CTA)", Valor: data.cta_clicks },
      { Métrica: "Duración Promedio Sesión (s)", Valor: data.summary.avg_duration_seconds ?? 0 },
      { Métrica: "Tiempo Promedio en Página (s)", Valor: data.avg_time_on_page ?? 0 },
    ];
    const wsSummary = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, "Resumen KPI");

    // 2. Dispositivos
    const devicesData = data.devices.map((d) => ({ Dispositivo: d.label, Cantidad: d.count, Porcentaje: `${d.percentage}%` }));
    const wsDevices = XLSX.utils.json_to_sheet(devicesData);
    XLSX.utils.book_append_sheet(wb, wsDevices, "Dispositivos");

    // 3. Ciudades
    const citiesData = data.cities.map((c) => ({ Ciudad: c.label, Cantidad: c.count, Porcentaje: `${c.percentage}%` }));
    const wsCities = XLSX.utils.json_to_sheet(citiesData);
    XLSX.utils.book_append_sheet(wb, wsCities, "Ciudades");

    // 4. Navegadores
    const browsersData = data.browsers.map((b) => ({ Navegador: formatBrowserLabel(b.label), Cantidad: b.count, Porcentaje: `${b.percentage}%` }));
    const wsBrowsers = XLSX.utils.json_to_sheet(browsersData);
    XLSX.utils.book_append_sheet(wb, wsBrowsers, "Navegadores");

    const profileData = (data.occupation_profiles?.audiences ?? []).map((item) => ({
      Perfil: item.label,
      Dispositivos: item.count,
      Porcentaje: `${item.percentage}%`,
    }));
    const wsProfiles = XLSX.utils.json_to_sheet(profileData);
    XLSX.utils.book_append_sheet(wb, wsProfiles, "Perfiles de Usuario");

    const specialistRolesData = (data.occupation_profiles?.cargos ?? data.occupation_profiles?.specialist_roles ?? []).map((item) => ({
      Cargo: item.label,
      Trabajadores: item.count,
      Porcentaje: `${item.percentage}%`,
    }));
    const wsSpecialistRoles = XLSX.utils.json_to_sheet(specialistRolesData);
    XLSX.utils.book_append_sheet(wb, wsSpecialistRoles, "Cargos Trabajadores");

    const companyNamesData = (data.occupation_profiles?.sectors ?? data.occupation_profiles?.company_names ?? []).map((item) => ({
      Sector: item.label,
      Registros: item.count,
      Porcentaje: `${item.percentage}%`,
    }));
    const wsCompanyNames = XLSX.utils.json_to_sheet(companyNamesData);
    XLSX.utils.book_append_sheet(wb, wsCompanyNames, "Sectores Registrados");

    const especialidadesData = (data.occupation_profiles?.especialidades ?? []).map((item) => ({
      Especialidad: item.label,
      Estudiantes: item.count,
      Porcentaje: `${item.percentage}%`,
    }));
    const wsEspecialidades = XLSX.utils.json_to_sheet(especialidadesData);
    XLSX.utils.book_append_sheet(wb, wsEspecialidades, "Especialidades Estudiantes");

    const trafficSourcesData = (data.traffic_sources ?? []).map((item) => ({
      Fuente: item.label,
      Sesiones: item.count,
      Porcentaje: `${item.percentage}%`,
    }));
    const wsTrafficSources = XLSX.utils.json_to_sheet(trafficSourcesData);
    XLSX.utils.book_append_sheet(wb, wsTrafficSources, "Fuentes de Tráfico");

    // 5. Páginas más vistas
    const pagesData = data.pages.map((p) => ({ Página: formatPageLabel(p.label), Vistas: p.count, Porcentaje: `${p.percentage}%` }));
    const wsPages = XLSX.utils.json_to_sheet(pagesData);
    XLSX.utils.book_append_sheet(wb, wsPages, "Páginas Más Vistas");

    // 6. Distribución por hora
    const hourlyData = data.hourly_distribution.map((h) => ({ Hora: h.label, Cantidad: h.count, Porcentaje: `${h.percentage}%` }));
    const wsHourly = XLSX.utils.json_to_sheet(hourlyData);
    XLSX.utils.book_append_sheet(wb, wsHourly, "Por Hora");

    // 7. Distribución por día
    const dailyData = data.daily_distribution.map((d) => ({ Día: d.label, Cantidad: d.count, Porcentaje: `${d.percentage}%` }));
    const wsDaily = XLSX.utils.json_to_sheet(dailyData);
    XLSX.utils.book_append_sheet(wb, wsDaily, "Por Día");

    // 8. Sesiones en el tiempo
    const timeData = data.sessions_over_time.map((s) => ({ Fecha: s.date, Sesiones: s.count }));
    const wsTime = XLSX.utils.json_to_sheet(timeData);
    XLSX.utils.book_append_sheet(wb, wsTime, "Evolución Temporal");

    XLSX.writeFile(wb, `Metricas_Usuario_${dateRange.startDate}_${dateRange.endDate}.xlsx`);
  };

  const defaultDateRange = getDefaultDateRange();
  const canResetDateRange =
    dateRange.startDate !== defaultDateRange.startDate ||
    dateRange.endDate !== defaultDateRange.endDate;

  return (
    <>
      <header className="border-b border-slate-200 bg-white px-4 py-3 md:px-6 flex justify-between items-center">
        <div>
          <h1 className="text-[11px] sm:text-xs font-bold tracking-widest text-slate-800 uppercase">
            Métricas de Usuario
          </h1>
          <h3 className="text-xs sm:text-sm font-medium text-gray-500">
            Dashboard de analytics y comportamiento de usuarios.
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportToExcel}
            disabled={!data}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Exportar Excel
          </button>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <label className="sr-only" htmlFor="analytics-start-date">Fecha de inicio</label>
            <input
              id="analytics-start-date"
              type="date"
              value={dateRange.startDate}
              max={dateRange.endDate}
              onChange={(event) => setDateRange((current) => ({ ...current, startDate: event.target.value }))}
              className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
            <span className="text-xs text-gray-400">a</span>
            <label className="sr-only" htmlFor="analytics-end-date">Fecha final</label>
            <input
              id="analytics-end-date"
              type="date"
              value={dateRange.endDate}
              min={dateRange.startDate}
              max={toDateInputValue(new Date())}
              onChange={(event) => setDateRange((current) => ({ ...current, endDate: event.target.value }))}
              className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
            <button
              type="button"
              onClick={() => setDateRange(getDefaultDateRange())}
              disabled={!canResetDateRange}
              className="rounded-lg border border-gray-500 px-2 py-1.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-gray-500"
            >
              Restablecer
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 py-5 md:py-8">
        <div className="container mx-auto px-4">
          {loading && <MetricsSkeleton />}

          {error && (
            <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-red-700">
              Error: {error}
            </div>
          )}

          {!loading && data && (
            <Skeleton>
            <div className="flex flex-col space-y-6">
              {/* Sesiones resumidas + Perfil */}
              <div className="order-3 grid grid-cols-1 gap-6 lg:grid-cols-5">
                <div className="self-start lg:col-span-3">
                  <SessionsSummaryBar items={[
                    { label: "Sesiones activas", value: activeSessions, subtitle: "En los últimos 15 min", tooltip: "Cantidad de usuarios que están navegando en este momento. Se considera activa una sesión que no ha cerrado la pestaña en los últimos 15 minutos.", icon: <Activity className="h-4 w-4 text-emerald-600" />, color: "#10b981" },
                    { label: "Total sesiones", value: data.summary.total_sessions, subtitle: `${data.summary.unique_visitors} visitantes únicos`, tooltip: "Número total de visitas realizadas en el periodo seleccionado. Un usuario puede generar múltiples sesiones si entra varias veces.", icon: <Users className="h-4 w-4 text-blue-600" />, color: "#2563eb" },
                    { label: "Páginas vistas", value: data.summary.total_page_views, tooltip: "Cantidad total de páginas cargadas. Incluye recargas y navegación entre /kapital y el landing.", icon: <Eye className="h-4 w-4 text-blue-600" />, color: "#06b6d4" },
                    { label: "Captación WhatsApp", value: data.cta_clicks, subtitle: "Clics a links de WhatsApp", tooltip: "Número de veces que los usuarios hicieron clic en botones de WhatsApp (CTA, productos, footer o chat flotante).", icon: <MousePointerClick className="h-4 w-4 text-blue-600" />, color: "#8b5cf6" },
                    { label: "Tiempo promedio", value: data.summary.avg_duration_seconds !== null && data.summary.avg_duration_seconds > 0 ? `${data.summary.avg_duration_seconds}s` : `${data.avg_time_on_page ?? 0}s`, subtitle: "Duración promedio por sesión", tooltip: "Tiempo medio que un usuario permanece en el sitio por sesión. Si no hay sesiones cerradas, muestra el tiempo promedio en página.", icon: <Clock className="h-4 w-4 text-blue-600" />, color: "#f59e0b" },
                  ]} />
                  <div className="mt-6">
                    <SectionCard title="Dispositivos" icon={<Monitor className="h-4 w-4" />} tooltip="Distribución del tráfico según el tipo de dispositivo.">
                      {data.devices.length === 0 ? <p className="text-sm text-gray-400">Sin datos</p> : data.devices.map((d) => (
                        <div key={d.label} className="mb-3 flex items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-50 text-gray-500">{deviceIcon(d.label)}</div>
                          <div className="min-w-0 flex-1"><div className="mb-1 flex items-center justify-between text-sm"><span className="truncate font-medium text-gray-700 capitalize">{d.label}</span><span className="text-gray-500">{d.count}</span></div><div className="h-2 w-full overflow-hidden rounded-full bg-gray-100"><div className={`h-full rounded-full ${deviceColor(d.label)}`} style={{ width: `${Math.min(d.percentage, 100)}%` }} /></div></div>
                        </div>
                      ))}
                    </SectionCard>
                  </div>
                </div>
                <SectionCard title="Perfil de usuarios" icon={<Users className="h-4 w-4" />} tooltip="Cada dispositivo se cuenta una sola vez según su elección más reciente dentro del periodo seleccionado." className="lg:col-span-2">
                  {!data.occupation_profiles || data.occupation_profiles.total_devices === 0 ? (
                    <p className="text-sm text-gray-400">Aún no hay perfiles registrados</p>
                  ) : (
                    <OccupationProfileBreakdown data={data.occupation_profiles} />
                  )}
                </SectionCard>
              </div>

              {/* Sesiones en el tiempo + Dispositivos */}
              <div className="order-4 grid grid-cols-1 gap-6">
                <div>
                  <SectionCard title="Sesiones en el tiempo" icon={<TrendingUp className="h-4 w-4" />} tooltip="Evolución diaria del tráfico en el periodo seleccionado.">
                    <SessionsChart data={data.sessions_over_time} />
                  </SectionCard>
                </div>
              </div>

              {/* Ciudades + Navegadores + Páginas */}
              <div className="order-5 grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Cities */}
                <SectionCard title="Ciudades principales" icon={<MapPin className="h-4 w-4" />} tooltip="Top 10 ciudades desde donde acceden los usuarios. Se obtiene mediante la IP del visitante.">
                  {data.cities.length === 0 ? (
                    <p className="text-sm text-gray-400">Sin datos de geolocalización</p>
                  ) : (
                    data.cities.map((c) => (
                      <ProgressBar key={c.label} label={c.label} count={c.count} percentage={c.percentage} color="bg-indigo-500" />
                    ))
                  )}
                </SectionCard>

                {/* Browsers */}
                <SectionCard title="Navegadores" icon={<Globe className="h-4 w-4" />} tooltip="Desglose de navegadores usados por los visitantes.">
                  {data.browsers.map((b) => {
                    const meta = getBrowserMeta(b.label);
                    return (
                      <ProgressBar
                        key={b.label}
                        label={formatBrowserLabel(b.label)}
                        count={b.count}
                        percentage={b.percentage}
                        color={meta.barClass}
                        barStyle={meta.barStyle}
                        icon={meta.icon}
                      />
                    );
                  })}
                </SectionCard>

                {/* Pages */}
                <SectionCard title="Páginas más vistas" icon={<BarChart3 className="h-4 w-4" />} tooltip="Páginas con mayor cantidad de vistas. Muestra qué contenido atrae más tráfico (/kapital, landing, etc.).">
                  {data.pages.map((p) => (
                    <ProgressBar key={p.label} label={formatPageLabel(p.label)} count={p.count} percentage={p.percentage} color="bg-violet-500" />
                  ))}
                </SectionCard>
              </div>

              <section className="order-1 space-y-3">
                <div>
                  <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">Embudo de Kapital</h2>
                  <p className="text-xs text-gray-500">Uso efectivo de la calculadora en el periodo seleccionado.</p>
                </div>
                <div className="grid grid-cols-1 gap-2 rounded-xl border border-gray-100 bg-white p-2 shadow-sm sm:grid-cols-2 lg:grid-cols-5">
                  <MetricCard title="Visitantes que inician" value={data.kapital_funnel?.users_started ?? 0} icon={<Users className="h-5 w-5" />} subtitle="Primera interacción manual" tooltip="Visitantes únicos que modificaron al menos un campo de Kapital." compact />
                  <MetricCard title="Tasa de activación" value={`${data.kapital_funnel?.activation_rate ?? 0}%`} icon={<Activity className="h-5 w-5 text-cyan-600" />} subtitle="Iniciaron entre visitantes" tooltip="Porcentaje de visitantes que iniciaron un cálculo." compact />
                  <MetricCard title="Cálculos iniciados" value={data.kapital_funnel?.started ?? 0} icon={<PlayCircle className="h-5 w-5" />} subtitle="Intentos iniciales validados" tooltip="Cálculos iniciales enviados correctamente." compact />
                  <MetricCard title="Cálculos completados" value={data.kapital_funnel?.completed ?? 0} icon={<CheckCircle2 className="h-5 w-5 text-emerald-600" />} subtitle="Sensibilizaciones realizadas" tooltip="Cálculos que alcanzaron una sensibilización correcta." compact />
                  <MetricCard title="Tasa de finalización" value={`${data.kapital_funnel?.completion_rate ?? 0}%`} icon={<Activity className="h-5 w-5 text-amber-600" />} subtitle="Completados entre iniciados" tooltip="Porcentaje de cálculos iniciados que terminaron correctamente." compact />
                </div>
              </section>

              {/* Retención */}
              <div className="order-2 grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
                <div className="lg:col-span-2">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">Retención de Kapital</h2>
                  <p className="text-xs text-gray-500">Personas identificadas por su cuenta o IP que llegan por primera vez o regresan a Kapital.</p>
                </div>
                <MetricCard
                      title="Visitantes nuevos"
                      value={data.kapital_retention?.new_users ?? 0}
                      icon={<UserPlus className="h-5 w-5 text-blue-600" />}
                      subtitle="Primera visita a Kapital"
                      tooltip="Cuentas o direcciones IP cuya primera visita histórica a Kapital ocurrió dentro del periodo seleccionado."
                />
                <MetricCard
                      title="Visitantes recurrentes"
                      value={data.kapital_retention?.recurring_users ?? 0}
                      icon={<RefreshCw className="h-5 w-5 text-emerald-600" />}
                      subtitle="Regresaron durante el periodo"
                      tooltip="Cuentas o direcciones IP que visitaron Kapital en el periodo seleccionado y ya tenían una visita anterior."
                />
              </div>

              {/* Origen + distribuciones en una misma fila */}
              <div className="order-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                <SectionCard title="Fuentes de tráfico" icon={<Share2 className="h-4 w-4" />} tooltip="Sesiones según su origen de llegada. Prioriza el utm_source del link de entrada (first-touch por sesión); sin UTM usa el dominio de referencia.">
                  {(data.traffic_sources ?? []).length === 0 ? (
                    <p className="text-sm text-gray-400">Sin datos de origen</p>
                  ) : (
                    data.traffic_sources.map((s, i) => (
                      <ProgressBar
                        key={s.label}
                        label={s.label}
                        count={s.count}
                        percentage={s.percentage}
                        color=""
                        barStyle={{ backgroundColor: sourceBarColor(s.label, i) }}
                      />
                    ))
                  )}
                </SectionCard>

                {/* Hourly */}
                <SectionCard title="Distribución por hora" icon={<Clock className="h-4 w-4" />} tooltip="Horas del día con mayor tráfico. Se agrupa por hora de inicio de sesión (hora de Lima, UTC-5).">
                  <div className="grid grid-cols-6 gap-2">
                    {data.hourly_distribution.map((h) => (
                      <div key={h.label} className="text-center">
                        <div className="mx-auto mb-1 flex h-16 w-full items-end justify-center rounded-md bg-gray-50 px-1">
                          <div
                            className="w-full rounded-sm bg-blue-500"
                            style={{ height: `${Math.max((h.count / Math.max(...data.hourly_distribution.map((d) => d.count), 1)) * 100, 4)}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-gray-500">{h.label}</span>
                      </div>
                    ))}
                  </div>
                </SectionCard>

                {/* Daily */}
                <SectionCard title="Distribución por día" icon={<Calendar className="h-4 w-4" />} tooltip="Tráfico distribuido por día de la semana.">
                  <div className="grid grid-cols-7 gap-2">
                    {data.daily_distribution.map((d) => (
                      <div key={d.label} className="text-center">
                        <div className="mx-auto mb-1 flex h-16 w-full items-end justify-center rounded-md bg-gray-50 px-1">
                          <div
                            className="w-full rounded-sm bg-emerald-500"
                            style={{ height: `${Math.max((d.count / Math.max(...data.daily_distribution.map((d) => d.count), 1)) * 100, 4)}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-gray-500">{d.label.slice(0, 3)}</span>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>
            </div>
            </Skeleton>
          )}
        </div>
      </div>

      <UtmLinkGenerator />
    </>
  );
};

export default AnalyticsPage;
